import {
  type GridRowId,
  type GridRowSelectionModel,
} from '@mui/x-data-grid-premium';
import { getRouteApi } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type TaxCodesSearch } from '~/routes/(main)/settings/masterdata/tax-codes/-searchParams';

import { type UUID } from '~/types/common';

import { useQuerySavedFilters } from '~/data/savedFilter';
import {
  useMutationBulkDeleteTaxCodes,
  useQueryTaxCodeFilterOptions,
} from '~/data/taxCode';

import {
  createUniversalSavedFilterUrlSync,
  UNIVERSAL_FILTER_TYPES,
} from '~/modules/universalFilters';

import { getTaxCodesFilterConfig } from './taxCodesFiltersConfig';

const TaxCodesRoute = getRouteApi('/(main)/settings/masterdata/tax-codes/');

const EMPTY_FILTER_OPTIONS = {
  codes: [],
  names: [],
  rates: [],
};

function createEmptySelection(): GridRowSelectionModel {
  return { ids: new Set<GridRowId>(), type: 'include' };
}

export function useTaxCodesPage() {
  const { t } = useTranslation('taxCodes');
  const navigate = TaxCodesRoute.useNavigate();
  const savedFilterId = TaxCodesRoute.useSearch({
    select: ({ savedFilterId }) => savedFilterId,
  });
  const taxCodeId = TaxCodesRoute.useSearch({
    select: ({ taxCodeId }) => taxCodeId,
  });
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isBulkDeleteConfirmOpen, setIsBulkDeleteConfirmOpen] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>(createEmptySelection);
  const selectedTaxCodeIds = useMemo(
    () => [...rowSelectionModel.ids].map(String) as UUID[],
    [rowSelectionModel],
  );
  const {
    error: bulkDeleteError,
    isPending: isBulkDeletePending,
    mutate: bulkDelete,
    reset: resetBulkDelete,
  } = useMutationBulkDeleteTaxCodes({
    onSuccess() {
      setRowSelectionModel(createEmptySelection());
    },
  });

  const { data: filterOptions = EMPTY_FILTER_OPTIONS } =
    useQueryTaxCodeFilterOptions({});

  const filterConfig = useMemo(
    () => getTaxCodesFilterConfig(t, filterOptions),
    [filterOptions, t],
  );

  const savedFilterUrlSync = useMemo(
    () =>
      createUniversalSavedFilterUrlSync<TaxCodesSearch>({
        savedFilterId,
        writeSearch: (writeSearch) => navigate({ search: writeSearch }),
      }),
    [navigate, savedFilterId],
  );

  const {
    data: savedFilters = [],
    error: savedFiltersError,
    isFetching: isSavedFiltersFetching,
    isLoading: isSavedFiltersLoading,
  } = useQuerySavedFilters(
    { type: UNIVERSAL_FILTER_TYPES.taxCodes },
    {
      enabled: Boolean(filterConfig.savedFilters?.enabled),
      meta: { suppressErrorForErrorCodes: [422] },
      select: (response) => response.data,
    },
  );

  const openCreateDialog = useCallback(() => {
    setIsCreateDialogOpen(true);
  }, []);

  const clearSelection = useCallback(() => {
    setRowSelectionModel(createEmptySelection());
    if (!isBulkDeletePending) {
      resetBulkDelete();
    }
  }, [isBulkDeletePending, resetBulkDelete]);

  const openBulkDeleteConfirm = useCallback(() => {
    resetBulkDelete();
    setIsBulkDeleteConfirmOpen(true);
  }, [resetBulkDelete]);

  const closeBulkDeleteConfirm = useCallback(() => {
    setIsBulkDeleteConfirmOpen(false);
  }, []);

  const confirmBulkDelete = useCallback(() => {
    setIsBulkDeleteConfirmOpen(false);
    bulkDelete({ ids: selectedTaxCodeIds });
  }, [bulkDelete, selectedTaxCodeIds]);

  const closeDialog = useCallback(() => {
    setIsCreateDialogOpen(false);

    if (taxCodeId) {
      void navigate({
        search: (prev) => ({
          ...prev,
          taxCodeId: undefined,
        }),
      });
    }
  }, [navigate, taxCodeId]);

  const savedFiltersState = useMemo(
    () => ({
      error: savedFiltersError,
      filters: savedFilters,
      isFetching: isSavedFiltersFetching,
      isLoading: isSavedFiltersLoading,
    }),
    [
      savedFilters,
      savedFiltersError,
      isSavedFiltersFetching,
      isSavedFiltersLoading,
    ],
  );

  return {
    bulkDeleteError,
    clearSelection,
    closeBulkDeleteConfirm,
    closeDialog,
    confirmBulkDelete,
    filterConfig,
    isBulkDeleteConfirmOpen,
    isBulkDeletePending,
    isDialogOpen: isCreateDialogOpen || Boolean(taxCodeId),
    openBulkDeleteConfirm,
    openCreateDialog,
    rowSelectionModel,
    savedFiltersState,
    savedFilterUrlSync,
    selectedTaxCodeIds,
    setRowSelectionModel,
    taxCodeId,
  };
}
